/**
 * Keeps the quantity grid's Issues column pinned left, at the end of the left-pinned block, whatever
 * layout the user has saved. The saved column state is restored in `onGridReady`
 * (`usePersistColState`) and carries the column's old pin and position, so the ColDef's own `pinned`
 * is not enough: this re-applies it once the grid has rendered its first rows, after that restore.
 * The new layout is then saved through the same persistence.
 */
import type { ColumnState, GridApi } from "ag-grid-community";
import type { AgGridReactProps } from "ag-grid-react";
import type { Instrument } from "../../const";
import { NEW_INSTRUMENT_ISSUE_COL_ID } from "./withIssueCell";

const SELECTION_COL_ID = "ag-Grid-SelectionColumn";

/**
 * Pins the Issues column left, right after the columns already pinned there (the selection column,
 * then Name or whatever else the user pinned). The selection column is pinned with it, so Issues
 * never takes its place at the grid's left edge; every other column keeps its state and order.
 */
export function pinIssueColumn(api: GridApi): void {
	const others = api.getColumnState().filter((column) => column.colId !== NEW_INSTRUMENT_ISSUE_COL_ID);
	// Within a pinned block columns display in column order, so the slot after the last one is its end.
	const slot = others.findLastIndex((column) => column.pinned === "left" || column.colId === SELECTION_COL_ID) + 1;
	const state: ColumnState[] = [
		...others
			.slice(0, slot)
			.map(({ colId }) => (colId === SELECTION_COL_ID ? { colId, pinned: "left" as const } : { colId })),
		{ colId: NEW_INSTRUMENT_ISSUE_COL_ID, pinned: "left" },
		...others.slice(slot).map(({ colId }) => ({ colId })),
	];
	api.applyColumnState({ state, applyOrder: true });
}

/** Spread into the quantity grid, after the props that restore the saved column state. */
export const pinnedIssueColumnGridProps = {
	onFirstDataRendered: (event) => {
		pinIssueColumn(event.api);
	},
} satisfies Partial<AgGridReactProps<Instrument>>;
